<template>
  <b-modal
    id="modal-add-product"
    ref="modalAddProduct"
    size="md"
    body-class="p-2"
    static
    hide-footer
    hide-header
    centered
    no-close-on-backdrop
    no-close-on-esc
    @hidden="resetForm"
  >
    <div class="h-min p-[16px]">
      <h3 class="text-black font-semibold text-[20px] text-center mb-[24px]">
        Tambah Data Produk
      </h3>
      <validation-observer
        ref="formRules"
        v-slot="{ handleSubmit }"
      >
        <b-form @submit.prevent="handleSubmit(submitForm)">
          <div
            class="mb-[16px]"
          >
            <label
              for="product-name"
              class="text-[#333333] text-[14px] mb-[8px]"
            >
              Nama Produk
            </label>
            <validation-provider
              #default="{errors}"
              name="Nama Produk"
              rules="required|min:3|max:60|unique_product_name"
              :debounce="1000"
            >
              <b-form-input
                id="product-name"
                v-model="form.product_name"
                placeholder="Masukkan nama produk"
                :formatter="formatProductName"
                :state="errors.length > 0 ? false : null"
                @keypress="validateProductNameInput"
              />
              <small
                v-if="errors[0]"
                class="text-danger"
              >{{ errors[0] }}</small>
              <small
                v-if="nameError"
                class="text-danger"
              >{{ nameError }}</small>
              <small class="text-muted float-right">{{ form.product_name.length }}/60</small>
            </validation-provider>
          </div>

          <div
            class="mb-[16px]"
          >
            <label
              for="product-name"
              class="text-[#333333] text-[14px] mb-[8px]"
            >
              Deskripsi Produk
            </label>
            <validation-provider
              #default="{errors}"
              name="Deskripsi Produk"
              rules="required|min:3|max:5000"
            >
              <b-form-textarea
                id="description"
                v-model="form.description"
                placeholder="Masukkan deskripsi produk"
                :state="errors.length > 0 ? false : null"
                rows="3"
              />
              <small
                v-if="errors[0]"
                class="text-danger"
              >{{ errors[0] }}</small>
              <small class="text-muted float-right">{{ form.description.length }}/5000</small>
            </validation-provider>
          </div>

          <div
            class="mb-[16px]"
          >
            <label
              for="sku"
              class="text-[#333333] text-[14px] mb-[8px]"
            >
              SKU
            </label>
            <validation-provider
              #default="{errors}"
              name="SKU"
              rules="required|min:3|max:25|unique_sku"
              :debounce="1000"
            >
              <b-form-input
                id="sku"
                v-model="form.sku"
                placeholder="Masukkan SKU produk"
                :formatter="formatSku"
                :state="errors.length > 0 ? false : null"
              />
              <small
                v-if="errors[0]"
                class="text-danger"
              >{{ errors[0] }}</small>
              <small class="text-muted float-right">{{ form.sku.length }}/25</small>
            </validation-provider>
          </div>

          <div
            class="mb-[16px]"
          >
            <label
              for="price"
              class="text-[#333333] text-[14px] mb-[8px]"
            >
              Harga
            </label>
            <validation-provider
              #default="{errors}"
              name="Harga"
              rules="required"
            >
              <b-input-group prepend="Rp">
                <b-form-input
                  id="price"
                  v-model="form.price"
                  placeholder="0"
                  :formatter="formatPrice"
                  :state="errors.length > 0 ? false : null"
                  @keypress="validatePriceInput"
                  @blur="formatPriceOnBlur"
                />
              </b-input-group>
              <small
                v-if="errors[0]"
                class="text-danger"
              >{{ errors[0] }}</small>
            </validation-provider>
          </div>

          <b-row
            class="mb-[24px]"
          >
            <b-col>
              <label
                for="length"
                class="text-[#333333] text-[14px] mb-[8px]"
              >
                Panjang
              </label>
              <validation-provider
                #default="{errors}"
                name="Panjang"
                rules="required"
              >
                <b-input-group>
                  <b-form-input
                    id="length"
                    ref="lengthInput"
                    v-model="form.length"
                    placeholder="0 cm"
                    type="text"
                    :formatter="formatDimensionInput"
                    :state="errors.length > 0 ? false : null"
                    @keypress="validateDimensionInput"
                    @input.native="setCursorAfterNumber"
                  />
                </b-input-group>
                <small
                  v-if="errors[0]"
                  class="text-danger"
                >{{ errors[0] }}</small>
              </validation-provider>
            </b-col>

            <b-col>
              <label
                for="width"
                class="text-[#333333] text-[14px] mb-[8px]"
              >
                Lebar
              </label>
              <validation-provider
                #default="{errors}"
                name="Lebar"
                rules="required"
              >
                <b-input-group>
                  <b-form-input
                    id="width"
                    ref="widthInput"
                    v-model="form.width"
                    placeholder="0 cm"
                    type="text"
                    :formatter="formatDimensionInput"
                    :state="errors.length > 0 ? false : null"
                    @keypress="validateDimensionInput"
                    @input.native="setCursorAfterNumber"
                  />
                </b-input-group>
                <small
                  v-if="errors[0]"
                  class="text-danger"
                >{{ errors[0] }}</small>
              </validation-provider>
            </b-col>

            <b-col>
              <label
                for="height"
                class="text-[#333333] text-[14px] mb-[8px]"
              >
                Tinggi
              </label>
              <validation-provider
                #default="{errors}"
                name="Tinggi"
                rules="required"
              >
                <b-input-group>
                  <b-form-input
                    id="height"
                    ref="heightInput"
                    v-model="form.height"
                    placeholder="0 cm"
                    type="text"
                    :formatter="formatDimensionInput"
                    :state="errors.length > 0 ? false : null"
                    @keypress="validateDimensionInput"
                    @input.native="setCursorAfterNumber"
                  />
                </b-input-group>
                <small
                  v-if="errors[0]"
                  class="text-danger"
                >{{ errors[0] }}</small>
              </validation-provider>
            </b-col>
          </b-row>

          <!-- Form Actions -->
          <div class="flex gap-[16px]">
            <b-button
              class="w-full"
              variant="outline-secondary"
              @click="hide"
            >
              Batal
            </b-button>
            <b-button
              class="w-full"
              type="submit"
              variant="primary"
              :disabled="isSubmitting"
              @click="preventDoubleClick"
            >
              <b-spinner
                v-if="isSubmitting"
                small
                class="mr-1"
              />
              {{ isSubmitting ? 'Menyimpan...' : 'Simpan Produk' }}
            </b-button>
          </div>
        </b-form>
      </validation-observer>
    </div>
  </b-modal>
</template>

<script>
import { ValidationObserver, ValidationProvider, extend } from 'vee-validate'
import {
  required, min, max, minValue,
} from '@validations'

export default {
  name: 'ModalAddProduct',
  components: {
    ValidationObserver,
    ValidationProvider,
  },
  data() {
    return {
      isSubmitting: false,
      lastClickTime: 0,
      form: {
        product_name: '',
        description: '',
        sku: '',
        price: '',
        length: '',
        width: '',
        height: '',
      },
      nameError: '',
    }
  },
  computed: {
    calculatedVolume() {
      if (this.form.length && this.form.width && this.form.height) {
        return (this.sanitizeDimension(this.form.length) * this.sanitizeDimension(this.form.width) * this.sanitizeDimension(this.form.height)) / 6000
      }
      return 0
    },
  },
  created() {
    // Validasi nama produk - cek ketersediaan nama produk
    extend('unique_product_name', {
      validate: async value => {
        if (!value || value.length < 3) return true

        try {
          const response = await this.$http_komship.get('/v1/product/check-name', {
            params: { product_name: value },
          })
          if (response.data.code === 1003) {
            return 'Nama produk sudah dipakai, yuk coba dengan nama lain'
          }
          return true
        } catch (error) {
          return 'Gagal memeriksa ketersediaan nama produk. Periksa koneksi internet.'
        }
      },
    })

    // Validasi SKU - cek ketersediaan SKU
    extend('unique_sku', {
      validate: async value => {
        if (!value || value.length < 3) return true

        try {
          const response = await this.$http_komship.get('/v1/product/check-sku', {
            params: { product_sku: value },
          })
          if (response.data.code === 1003) {
            return 'SKU sudah dipakai, yuk coba dengan SKU lain'
          }
          return true
        } catch (error) {
          return 'Gagal memeriksa ketersediaan SKU. Periksa koneksi internet.'
        }
      },
    })
  },
  methods: {
    show() {
      this.$refs.modalAddProduct.show()
    },
    hide() {
      this.$refs.modalAddProduct.hide()
    },
    resetForm() {
      this.form = {
        product_name: '',
        description: '',
        sku: '',
        price: '',
        weight: '',
        weight_pbv: '',
        length: '',
        width: '',
        height: '',
      }
      this.isSubmitting = false
      this.lastClickTime = 0
      this.$nextTick(() => {
        this.$refs.formRules.reset()
      })
    },

    preventDoubleClick(event) {
      const now = Date.now()
      if (this.isSubmitting || (now - this.lastClickTime < 1000)) {
        event.preventDefault()
        event.stopPropagation()
        return false
      }
      this.lastClickTime = now
      return true
    },

    formatProductName(value) {
      return String(value)
        .replace(/[/=:;&]/g, '')
        .substring(0, 60)
    },
    formatDescription(value) {
      return String(value).substring(0, 5000)
    },
    formatSku(value) {
      return String(value)
        .replace(/[^a-zA-Z0-9\-_]/g, '')
        .substring(0, 25)
        .toUpperCase()
    },
    formatPrice(value) {
      const numericValue = String(value).replace(/[^\d]/g, '')
      return numericValue.replace(/\B(?=(\d{3})+(?!\d))/g, '.')
    },

    validateProductNameInput(e) {
      const forbiddenChars = [47, 61, 58, 59, 38]
      if (forbiddenChars.includes(e.keyCode)) {
        e.preventDefault()
        this.nameError = 'Hindari menggunakan simbol (/) (=) (:) (;) (&) pada nama produk'
      } else {
        this.nameError = ''
      }
    },
    validatePriceInput(e) {
      const keyCode = e.keyCode || e.which
      if (keyCode < 48 || keyCode > 57) {
        e.preventDefault()
      }
    },
    validateDimensionInput(e) {
      const keyCode = e.keyCode || e.which

      const isNumber = keyCode >= 48 && keyCode <= 57
      const isDecimalPoint = keyCode === 46

      if (!isNumber && !isDecimalPoint) {
        e.preventDefault()
        return
      }

      const currentValue = e.target.value.replace(/\s*cm\s*$/gi, '').trim()

      if (isDecimalPoint && currentValue.includes('.')) {
        e.preventDefault()
      }
    },

    formatDimensionInput(value) {
      if (!value) return ''

      let cleanValue = String(value).replace(/[^0-9.]/g, '')

      const parts = cleanValue.split('.')
      if (parts.length > 2) {
        cleanValue = `${parts[0]}.${parts.slice(1).join('')}`
      }

      if (cleanValue.length > 6) {
        cleanValue = cleanValue.substring(0, 6)
      }

      if (cleanValue) {
        return `${cleanValue} cm`
      }

      return cleanValue
    },

    setCursorAfterNumber(event) {
      const { target } = event
      const { value } = target

      if (event && value.length > 3) {
        this.$nextTick(() => {
          target.setSelectionRange(value.length - 3, value.length - 3)
        })
      }
    },
    formatPriceOnBlur() {
      if (this.form.price) {
        let numericValue = String(this.form.price).replace(/[^\d]/g, '')
        if (parseInt(numericValue, 10) < 1000) {
          numericValue = '1000'
        }
        this.form.price = numericValue.replace(/\B(?=(\d{3})+(?!\d))/g, '.')
      }
    },

    async submitForm() {
      if (this.isSubmitting) return

      this.isSubmitting = true
      try {
        const sanitizedData = {
          product_name: this.sanitizeString(this.form.product_name),
          description: this.sanitizeString(this.form.description),
          sku: this.sanitizeString(this.form.sku).toUpperCase(),
          price: this.sanitizeNumeric(this.form.price),
          weight: this.calculatedVolume.toFixed(3) * 1000,
          stock: 0,
          length: this.sanitizeDimension(this.form.length),
          width: this.sanitizeDimension(this.form.width),
          height: this.sanitizeDimension(this.form.height),
        }

        const { data } = await this.$http_komship.post('/v1/product/create/1', sanitizedData)

        this.$emit('product-created', data.data.product_id)
        this.$toast_success({
          message: 'Produk berhasil ditambahkan',
        })
        this.hide()
      } catch (error) {
        this.$toast_error({ message: 'Gagal menambahkan produk' })
      } finally {
        this.isSubmitting = false
      }
    },

    sanitizeString(value) {
      return String(value).trim().replace(/\s+/g, ' ')
    },
    sanitizeNumeric(value) {
      return parseInt(String(value).replace(/[^\d]/g, ''), 10) || 0
    },
    sanitizeDimension(value) {
      if (!value) return 0
      let cleanValue = String(value).replace(/\s*cm\s*$/gi, '').trim()
      cleanValue = cleanValue.replace(/[^\d.]/g, '')
      return parseFloat(cleanValue) || 0
    },
  },
}
</script>
